<template>
  <article
    class="group relative flex h-full flex-col"
    :class="[$style.cardContainer]"
    data-spec="product-card-content"
  >
    <div class="mb-auto p-16" data-spec="main">
      <div
        v-if="
          withMarketingTags &&
          product.marketingTags &&
          product.marketingTags.length > 0
        "
        class="flex mb-8"
        :class="{
          [$style.marketingTag]: options.format !== 'compact',
        }"
      >
        <MarketingTag
          v-if="product.marketingTags?.[0]"
          :tag="product.marketingTags[0]"
        />
      </div>
      <div class="flex">
        <div
          class="flex max-w-full grow content-start justify-center gap-8"
          :class="[
            $style.cardWrapper,
            {
              [$style.isBackbox]: product.granularity === 'backbox',
              'flex-row-reverse items-start': options.format === 'compact',
              'flex-col-reverse': options.format !== 'compact',
            },
          ]"
          data-spec="image+main-information"
        >
          <div
            class="flex min-w-0 grow flex-col items-start gap-6"
            data-spec="product-info"
          >
            <div class="flex flex-col gap-2 max-w-full" data-spec="titles">
              <ProductCardDeal
                v-if="withDeal"
                class="relative mb-6"
                :deal-tag
              />
              <h3>
                <RevButtonBase
                  ref="link"
                  class="after:focus-visible-outline-default-hi after:rounded-lg after:absolute after:inset-0 after:bg-transparent focus-visible:outline-none"
                  :to="product.url"
                  @click="cardClickHandler"
                >
                  <span
                    class="body-1-bold line-clamp-2"
                    data-test="product-title"
                  >
                    {{ product.title }}
                  </span>
                  <span
                    v-if="enrichedDescription"
                    class="text-static-default-low body-2"
                    :class="
                      options.extendedDescription
                        ? 'line-clamp-3'
                        : 'line-clamp-2'
                    "
                    data-test="product-description"
                  >
                    {{ enrichedDescription }}
                  </span>
                </RevButtonBase>
              </h3>

              <span v-if="product.warranty && withWarranty" class="body-2">
                {{
                  i18n(translations.productCardWarranty, {
                    warranty: product.warranty,
                  })
                }}
                {{ i18n(translations.months) }}
              </span>
            </div>

            <ProductCardRatings
              v-if="
                product.reviewRating?.count && product.reviewRating?.average
              "
              :count="product.reviewRating.count"
              data-spec="rating"
              :score="product.reviewRating.average"
            />

            <div
              v-if="product.specifications && product.specifications.length > 0"
            >
              <ProductSpecs :specifications="product.specifications" />
            </div>
            <div class="flex w-full items-start justify-between gap-x-6">
              <div v-if="product.price" data-spec="price-information">
                <div
                  v-if="product.productType === 'Brand new'"
                  class="text-static-default-low caption"
                  data-test="product-brand-new"
                >
                  <RevTag
                    :label="i18n(translations.brandNewLabel)"
                    variant="info"
                  />
                </div>
                <div
                  v-else-if="options.withStartingFrom"
                  class="text-static-default-low caption"
                  data-test="product-starting-from"
                >
                  {{ i18n(translations.startingFrom) }}
                </div>
                <ProductPrice
                  :after-tradein-price="product.discountedPrice"
                  :deal="withDeal ? eligibleDeal : undefined"
                  :new-price="product.priceNew"
                  :price="product.price"
                />
              </div>
              <AddToCartButton
                v-if="shouldShowAddToCart && options.format === 'compact'"
                :id="product.listingPublicId"
                :format="options.format"
                :product
                :trackingData="
                  productToTrackingData(product.rawProduct, {
                    ...options.tracking,
                    granularity: product.granularity,
                    position: options.position,
                  })
                "
                zone="product_block_primary"
                @add-to-cart="addToCart"
              />
            </div>
          </div>

          <div
            class="flex flex-col items-center justify-center gap-4 shrink-0"
            data-spec="image+colors"
          >
            <RevIllustration
              alt=""
              data-test="product-card-image"
              :height="imageSize"
              :sizes="`${imageSize}px`"
              :src="product.imageSrc"
              :width="imageSize"
            />

            <ColorSwatches
              v-if="product.colorSwatches"
              :colors="product.colorSwatches"
              data-test="product-color-swatches"
            />
          </div>
        </div>
      </div>
    </div>

    <ProductComparisonCheckbox
      v-if="comparison?.isComparisonEnabled"
      class="absolute bottom-8 right-8 lg:bottom-auto lg:top-8"
      :is-checked="isProductCompared"
      :is-comparison-add-disabled="
        comparison.isComparisonAddProductDisabled ?? false
      "
      :is-responsive-comparison-enabled="
        comparison.isResponsiveComparisonEnabled ?? false
      "
      @change="emit('toggle-comparison-product', product.listingId)"
    />

    <ProductCardPaymentAdvertisement
      :base-price="product.price"
      class="mx-16 mb-16 min-h-40 md:min-h-68"
      :with-payment-advertisement
    />

    <div
      v-if="shouldShowAddToCart && options.format !== 'compact'"
      class="p-16 pt-0"
    >
      <AddToCartButton
        :id="product.listingPublicId"
        :format="options.format"
        :product
        :text="i18n(translations.addToCart)"
        :trackingData="
          productToTrackingData(product.rawProduct, {
            ...options.tracking,
            granularity: product.granularity,
            position: options.position,
          })
        "
        zone="product_block_primary"
        @add-to-cart="addToCart"
      />
    </div>
  </article>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import type { Deal } from '@backmarket/nuxt-layer-cms/products/algolia/types'
import ProductCardPaymentAdvertisement from '@backmarket/nuxt-layer-payment/advertising/components/ProductCardPaymentAdvertisement/ProductCardPaymentAdvertisement.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'
import { RevTag } from '@ds/components/Tag'

import { useProductCardDeal } from '../../composables/useProductCardDeal'
import {
  getReferrerRoute,
  useProductClickStore,
} from '../../composables/useProductClickStore'
import {
  type ExtraTrackingData,
  productToTrackingData,
} from '../../composables/useProductTracking'
import type { ComparisonProduct } from '../../models/product'
import AddToCartButton from '../AddToCartButton/AddToCartButton.vue'
import ColorSwatches from '../ColorSwatches.vue'
import ProductCardDeal from '../ProductCardDeal.vue'
import ProductCardRatings from '../ProductCardRatings.vue'
import ProductComparisonCheckbox from '../ProductComparison/components/ProductComparisonCheckbox.vue'
import ProductPrice from '../ProductPrice/ProductPrice.vue'
import ProductSpecs from '../ProductSpecs.vue'

import { BATTERY_SPECIAL_OFFER } from './ProductCard.constants'
import translations from './ProductCard.translations'
import type { ProductCardProduct } from './ProductCard.types'
import MarketingTag from './components/MarketingTag.vue'

const {
  comparison,
  product,
  options = {},
} = defineProps<{
  product: ProductCardProduct
  options?: {
    withAddToCart?: boolean
    position?: number
    withStartingFrom?: boolean
    extendedDescription?: boolean
    format?: 'default' | 'compact'
    withMarketingTags?: boolean
    withDeal?: boolean
    excludedDealTypes?: Deal['type'][]
    withWarranty?: boolean
    withPaymentAdvertisement?: boolean
    forceHideAddToCart?: boolean
    tracking?: ExtraTrackingData
  }
  comparison?: {
    isComparisonEnabled?: boolean
    isComparisonAddProductDisabled?: boolean
    isResponsiveComparisonEnabled?: boolean
    comparisonProducts?: ComparisonProduct[]
  }
}>()

const {
  withMarketingTags = true,
  withDeal = true,
  excludedDealTypes = [],
  withWarranty = true,
  withPaymentAdvertisement = false,
} = options

const emit = defineEmits([
  'add-to-cart',
  'toggle-comparison-product',
  'link-click',
])

const i18n = useI18n()

const { dealTag, eligibleDeal } = useProductCardDeal(
  product.deals,
  product.granularity,
  withDeal,
  excludedDealTypes,
)

function addToCart({
  status,
  error,
}: {
  status: string | undefined
  error?: unknown
}) {
  emit('add-to-cart', { status, error })
}

const imageSize = computed(() => {
  if (options.format === 'compact') return 80

  return product.granularity === 'backbox' ? 224 : 128
})

const isProductCompared = computed(() => {
  return (
    comparison?.comparisonProducts?.some(
      (p) => p.listingId === product.listingId,
    ) || false
  )
})

const shouldShowAddToCart = computed(() => {
  if (options.forceHideAddToCart) return false

  return options.withAddToCart || product.granularity === 'backbox'
})

// TODO [SR-5653]: The backend will eventually return attributes as a proper array, which will
// remove the need for the ugly split(' • ') on the description string.
const enrichedDescription = computed(() => {
  if (product.granularity === 'parent') return undefined

  let backboxLabel: string | undefined
  if (product.granularity === 'backbox') {
    backboxLabel =
      product.productType === 'Brand new'
        ? i18n(translations.brandNewLabel)
        : product.grade?.name
  }

  return [
    backboxLabel,
    ...(product.description?.split(' • ') ?? []),
    product.granularity === 'backbox' &&
      product.specialOfferType === BATTERY_SPECIAL_OFFER &&
      i18n(translations.specialOfferType7),
  ]
    .filter(Boolean)
    .join(' • ')
})

const route = useRoute()
const storeProductCardData = useProductClickStore()
function cardClickHandler(event: MouseEvent) {
  storeProductCardData({
    listingId: product.listingPublicId ?? '',
    price: product.price.amount,
    referrerRoute: getReferrerRoute(route),
    category: product.rawProduct.category,
  })
  emit('link-click', event)
}
</script>

<style module>
.cardContainer {
  container-type: inline-size;
  container-name: product-card;
}

@container product-card (width <= 326px) {
  .marketingTag {
    min-height: 16px;
  }
}

@container product-card (width > 326px) {
  .cardWrapper:not(.isBackbox) {
    flex-direction: row-reverse;
  }
}
</style>
